import { useQuerystring } from "$routes/_lib/use-querystring";
import { addDays, isAfter, isBefore } from "date-fns";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  type InvoiceFilterStatus,
  InvoiceStatus,
} from "../InvoiceFilters/types";
import type { useFetchPatientInvoices } from "./useFetchPatientInvoices";

const mapFilterToInvoiceStatuses = (
  value: InvoiceFilterStatus,
): InvoiceStatus[] => {
  switch (value) {
    case "ALL":
      return [
        InvoiceStatus.Draft,
        InvoiceStatus.Paid,
        InvoiceStatus.PartiallyPaid,
        InvoiceStatus.PartiallyRefunded,
        InvoiceStatus.Pending,
        InvoiceStatus.Refunded,
        InvoiceStatus.Unpaid,
        InvoiceStatus.Void,
      ];
    case "PAID":
      return [InvoiceStatus.Paid, InvoiceStatus.PartiallyRefunded];
    case "PENDING":
      return [InvoiceStatus.Pending];
    case "UNPAID":
      return [
        InvoiceStatus.Unpaid,
        InvoiceStatus.PartiallyPaid,
        InvoiceStatus.Draft,
      ];
    case "REFUNDED":
      return [InvoiceStatus.Refunded];
    case "VOID":
      return [InvoiceStatus.Void];
    default:
      return [
        InvoiceStatus.Draft,
        InvoiceStatus.Paid,
        InvoiceStatus.PartiallyPaid,
        InvoiceStatus.PartiallyRefunded,
        InvoiceStatus.Pending,
        InvoiceStatus.Refunded,
        InvoiceStatus.Unpaid,
        InvoiceStatus.Void,
      ];
  }
};

export default function useFilteredInvoices(
  invoices: ReturnType<typeof useFetchPatientInvoices>["invoices"],
) {
  const { fromDate, toDate, invoiceStatus } = useQuerystring() as {
    fromDate: string;
    toDate: string;
    invoiceStatus: InvoiceFilterStatus;
  };
  const navigate = useNavigate();
  const location = useLocation();

  const replaceQueryParam = (key: string) => (value: string) => {
    const existingParams = new URLSearchParams(location.search);
    existingParams.set(key, value);
    navigate(
      { pathname: location.pathname, search: existingParams.toString() },
      { replace: true },
    );
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      if (
        invoiceStatus &&
        !mapFilterToInvoiceStatuses(invoiceStatus).includes(
          invoice?.invoiceSummary?.invoiceStatus,
        )
      ) {
        return false;
      }

      if (
        fromDate &&
        isBefore(new Date(invoice?.invoiceSummary.created), new Date(fromDate))
      ) {
        return false;
      }

      if (
        toDate &&
        isAfter(
          new Date(invoice?.invoiceSummary.created),
          addDays(new Date(toDate), 1),
        )
      ) {
        return false;
      }

      return true;
    });
  }, [invoices, fromDate, toDate, invoiceStatus]);

  return {
    filteredInvoices,
    fromDate,
    toDate,
    invoiceStatus,
    setFromDate: replaceQueryParam("fromDate"),
    setToDate: replaceQueryParam("toDate"),
    setInvoiceStatus: replaceQueryParam("invoiceStatus"),
  };
}
